import type { CSSProperties } from "react";
import { detectEnvironment } from "@zektec/environment-utils";

const DEFAULT_STAGE_INDICATORS = ["stage", "staging", "release-stage", "pages.dev"];
const DEFAULT_LOCAL_INDICATORS = ["localhost", ".local", "127.0.0.1"];

type Placement = "bottom-right" | "bottom-left" | "bottom-center";

export type EnvironmentTagProps = {
  environment?: string;
  isStaging?: boolean;
  isLocal?: boolean;
  origin?: string;
  stageIndicators?: string[];
  localIndicators?: string[];
  showInProduction?: boolean;
  version?: string | null;
  placement?: Placement;
  offset?: number;
  zIndex?: number;
  style?: CSSProperties;
};

function detectFromText(value: string | undefined, indicators: string[]): boolean {
  if (!value) {
    return false;
  }

  const normalized = value.toLowerCase();
  return indicators.some((indicator) => normalized.includes(indicator.toLowerCase()));
}

function resolveLabels({
  environment,
  origin,
  stageIndicators,
  localIndicators,
  isStaging,
  isLocal
}: Pick<
  EnvironmentTagProps,
  "environment" | "origin" | "stageIndicators" | "localIndicators" | "isStaging" | "isLocal"
>): string[] {
  const labels: string[] = [];
  const detectedEnvironment = detectEnvironment({
    mode: environment,
    origin,
    stagingIndicators: stageIndicators,
    localHostIndicators: localIndicators
  });

  const shouldShowStaging =
    Boolean(isStaging) ||
    detectedEnvironment === "staging" ||
    detectFromText(origin, stageIndicators || DEFAULT_STAGE_INDICATORS);

  const shouldShowLocal =
    Boolean(isLocal) ||
    detectedEnvironment === "local" ||
    detectedEnvironment === "development" ||
    detectedEnvironment === "test" ||
    detectFromText(origin, localIndicators || DEFAULT_LOCAL_INDICATORS);

  if (shouldShowStaging) {
    labels.push("STAGING");
  }

  if (shouldShowLocal) {
    labels.push("LOCAL");
  }

  return labels;
}

function placementStyle(placement: Placement, offset: number): CSSProperties {
  if (placement === "bottom-left") {
    return { left: offset, bottom: offset };
  }

  if (placement === "bottom-center") {
    return {
      left: "50%",
      bottom: offset,
      transform: "translateX(-50%)"
    };
  }

  return { right: offset, bottom: offset };
}

export function EnvironmentTag({
  environment,
  isStaging = false,
  isLocal = false,
  origin,
  stageIndicators,
  localIndicators,
  showInProduction = false,
  version,
  placement = "bottom-right",
  offset = 12,
  zIndex = 9999,
  style
}: EnvironmentTagProps) {
  const safeOrigin = origin || (typeof window !== "undefined" ? window.location.origin : "");
  const labels = resolveLabels({
    environment,
    origin: safeOrigin,
    stageIndicators,
    localIndicators,
    isStaging,
    isLocal
  });

  if (!showInProduction && labels.length === 0) {
    return null;
  }

  const text = labels.length ? labels.join(" + ") : "NON-PRODUCTION";
  const normalizedVersion = version?.trim();
  const displayText = normalizedVersion ? `${text} v${normalizedVersion}` : text;

  return (
    <div
      aria-label="Environment tag"
      style={{
        position: "fixed",
        ...placementStyle(placement, offset),
        zIndex,
        fontFamily:
          "ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 0.8,
        textTransform: "uppercase",
        color: "#7a4300",
        backgroundColor: "#fff4d6",
        border: "1px solid #f4c06b",
        borderRadius: 9999,
        padding: "6px 10px",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.12)",
        pointerEvents: "none",
        ...style
      }}
    >
      {displayText}
    </div>
  );
}

export default EnvironmentTag;
